/**
 * prohibited-pairings.ts - Manage prohibited pairings for players
 *
 * Handles UI for selecting which players cannot be paired against each other.
 * This is useful for same-club restrictions, family members, etc.
 */

import type { Player, ProhibitedPairing } from '../../../types/api';
import { API_BASE_URL } from '../../utils/constants';
import { t } from '../../utils/i18n-helper';

/**
 * Open the prohibited pairings modal for a player
 */
export async function openProhibitedPairingsModal(tournamentId: string, playerId: string): Promise<void> {
  try {
    // Fetch tournament data to get all players
    const tournamentResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include'
    });

    if (!tournamentResponse.ok) {
      throw new Error('Failed to load tournament');
    }

    const tournament = await tournamentResponse.json();
    const currentPlayer = tournament.players.find((p: Player) => p.id === playerId);

    if (!currentPlayer) {
      throw new Error('Player not found');
    }

    // Fetch existing prohibited pairings for this player
    const prohibitedResponse = await fetch(`${API_BASE_URL}/${tournamentId}/players/${playerId}/prohibited-pairings`, {
      credentials: 'include'
    });

    const prohibitedPairings: ProhibitedPairing[] = prohibitedResponse.ok
      ? await prohibitedResponse.json()
      : [];

    // Get IDs of players already prohibited
    const prohibitedIds = new Set(prohibitedPairings.map(p => p.otherPlayerId));

    // Create modal HTML
    const modalHtml = `
      <div id="prohibited-pairings-modal" class="modal" style="display: block;">
        <div class="modal-content modal-medium">
          <span class="close" onclick="closeProhibitedPairingsModal()">&times;</span>
          <h2>${t('prohibitedPairings.title', 'Prohibited Pairings')}</h2>
          <p class="modal-subtitle">
            ${t('prohibitedPairings.subtitle', 'Select players who cannot be paired against')}
            <strong>${currentPlayer.firstName} ${currentPlayer.lastName}</strong>
          </p>

          <div class="prohibited-pairings-search">
            <input type="text" id="prohibited-search-input" placeholder="${t('prohibitedPairings.searchPlaceholder', 'Search players...')}" class="form-input">
          </div>

          <div class="prohibited-pairings-list" id="prohibited-pairings-list">
            ${tournament.players
              .filter((p: Player) => p.id !== playerId)
              .map((p: Player) => `
                <div class="prohibited-player-row ${prohibitedIds.has(p.id) ? 'prohibited' : ''}" data-player-id="${p.id}" data-name="${p.firstName.toLowerCase()} ${p.lastName.toLowerCase()}">
                  <label class="checkbox-label">
                    <input type="checkbox" ${prohibitedIds.has(p.id) ? 'checked' : ''} onchange="toggleProhibitedPairing('${tournamentId}', '${playerId}', '${p.id}', this.checked)">
                    <span class="player-info">
                      ${p.title ? `<span class="player-title-badge">${p.title}</span>` : ''}
                      ${p.firstName} ${p.lastName}
                      ${p.rating ? `<span class="player-rating-inline">${p.rating}</span>` : ''}
                      ${p.club ? `<span class="player-club-inline">${p.club}</span>` : ''}
                    </span>
                  </label>
                </div>
              `).join('')}
          </div>

          <div class="modal-actions">
            <button class="btn btn-secondary" onclick="closeProhibitedPairingsModal()">
              ${t('common.close', 'Close')}
            </button>
          </div>
        </div>
      </div>
    `;

    // Remove existing modal if any
    const existingModal = document.getElementById('prohibited-pairings-modal');
    if (existingModal) {
      existingModal.remove();
    }

    // Add modal to document
    document.body.insertAdjacentHTML('beforeend', modalHtml);

    // Set up search filtering
    const searchInput = document.getElementById('prohibited-search-input') as HTMLInputElement;
    if (searchInput) {
      searchInput.focus();
      searchInput.addEventListener('input', () => {
        filterProhibitedPlayerList(searchInput.value);
      });
    }

  } catch (error) {
    console.error('Error opening prohibited pairings modal:', error);
    alert(t('prohibitedPairings.error', 'Failed to load prohibited pairings'));
  }
}

/**
 * Close the prohibited pairings modal
 */
export function closeProhibitedPairingsModal(): void {
  const modal = document.getElementById('prohibited-pairings-modal');
  if (modal) {
    modal.remove();
  }
}

/**
 * Filter the prohibited player list by search query
 */
function filterProhibitedPlayerList(query: string): void {
  const list = document.getElementById('prohibited-pairings-list');
  if (!list) return;

  const normalizedQuery = query.toLowerCase().trim();
  const rows = list.querySelectorAll('.prohibited-player-row');

  rows.forEach(row => {
    const name = row.getAttribute('data-name') || '';
    if (normalizedQuery === '' || name.includes(normalizedQuery)) {
      (row as HTMLElement).style.display = '';
    } else {
      (row as HTMLElement).style.display = 'none';
    }
  });
}

/**
 * Toggle a prohibited pairing
 */
export async function toggleProhibitedPairing(
  tournamentId: string,
  playerId: string,
  otherPlayerId: string,
  isProhibited: boolean
): Promise<void> {
  try {
    if (isProhibited) {
      // Add prohibited pairing
      const response = await fetch(`${API_BASE_URL}/${tournamentId}/prohibited-pairings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          player1Id: playerId,
          player2Id: otherPlayerId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add prohibited pairing');
      }

      // Update UI
      const row = document.querySelector(`.prohibited-player-row[data-player-id="${otherPlayerId}"]`);
      if (row) {
        row.classList.add('prohibited');
      }

    } else {
      // First, get the pairing ID
      const pairingsResponse = await fetch(`${API_BASE_URL}/${tournamentId}/players/${playerId}/prohibited-pairings`, {
        credentials: 'include'
      });

      if (!pairingsResponse.ok) {
        throw new Error('Failed to fetch prohibited pairings');
      }

      const pairings: ProhibitedPairing[] = await pairingsResponse.json();
      const pairing = pairings.find(p => p.otherPlayerId === otherPlayerId);

      if (pairing) {
        // Remove prohibited pairing
        const response = await fetch(`${API_BASE_URL}/${tournamentId}/prohibited-pairings/${pairing.id}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to remove prohibited pairing');
        }
      }

      // Update UI
      const row = document.querySelector(`.prohibited-player-row[data-player-id="${otherPlayerId}"]`);
      if (row) {
        row.classList.remove('prohibited');
      }
    }

  } catch (error) {
    console.error('Error toggling prohibited pairing:', error);
    alert(error instanceof Error ? error.message : t('prohibitedPairings.toggleError', 'Failed to update prohibited pairing'));

    // Revert checkbox state on error
    const checkbox = document.querySelector(`.prohibited-player-row[data-player-id="${otherPlayerId}"] input[type="checkbox"]`) as HTMLInputElement;
    if (checkbox) {
      checkbox.checked = !isProhibited;
    }
  }
}

// Export for window global access
if (typeof window !== 'undefined') {
  (window as any).openProhibitedPairingsModal = openProhibitedPairingsModal;
  (window as any).closeProhibitedPairingsModal = closeProhibitedPairingsModal;
  (window as any).toggleProhibitedPairing = toggleProhibitedPairing;
}
